import Intl from '@fx-ui/intl';
import { Check, Combo, Dialog, Form, IRuleDescriptor, Message, Radio } from '@fx-ui/fine-design';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { ValidCode, I18nPhone } from '@fx-ui/platform-sdk';
import React, { useEffect, useRef, useState } from 'react';
import { API, EMPTY_FN, UGC_MAX_LENGTH } from '~/constants';
import {
    assign,
    get,
    getApi,
    getAvailablePersonaData,
    getCorpIndustry,
    getFavoriteOptions,
    getJobs,
    getPersonaCollectAvailableFields,
    hasFunctionPlatformPhone,
    isFavoritesStandalone,
    isNumber,
    map,
    post,
    size,
    trim
} from '~/utils';
import { fetchPersonaLabel } from '~/request/persona';

interface IInfoComplete {
    personalInfo: any;
    tenantId: string;
    visible: boolean;
    onClose: () => void;
    onUpdate: (personalInfo: any) => void;
}

const { COMPANY_NAME } = UGC_MAX_LENGTH.USER;

const InfoCompleteDialog: React.FunctionComponent<IInfoComplete> = (props: IInfoComplete) => {
    const { personalInfo, tenantId, visible, onClose, onUpdate } = props;

    const [phoneValue, setPhoneValue] = useState<string>('');
    const [codeValue, setCodeValue] = useState<string>('');
    const [originLabelData, setOriginLabelData] = useState<Record<string, any>>({});
    const [industry, setIndustry] = useState<string>(personalInfo.industry);
    const suiteId = personalInfo.suite_id;

    useEffect(() => {
        // 当关闭完善信息弹窗后再次打开时，重置行业的值，防止老数据干扰
        if (visible) {
            setIndustry(personalInfo.industry);
        }
    }, [visible]);

    const formValue = {
        job: personalInfo.job,
        company: personalInfo.company,
        industry: personalInfo.industry,
        favorites: personalInfo.favorites,
        corp_size: personalInfo.corp_size
    };

    const formRef = useRef<any>();
    const validCodeRef = useRef<any>();
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetchPersonaLabel(suiteId).then((res) => {
            setOriginLabelData(get(res, 'data'));
        });
    }, [suiteId]);

    useEffect(() => {
        if (!visible) {
            setPhoneValue('');
            setCodeValue('');
        }
    }, [visible]);

    const getCorpSizeItem = () => {
        return [
            { value: '1-10' },
            { value: '11-50' },
            { value: '51-200' },
            { value: '201-500' },
            { value: '500+' }
        ];
    };

    const dealValidCodeBtnClick = () => {
        if (!phoneValue) {
            Message.add({
                messageType: 'toast',
                type: 'warning',
                message: Intl.get('phone.invalid.warning')
            });
            return;
        }
        return post(getApi(API.authentication.send_sms), {
            phone: phoneValue
        }).then(() => {
            validCodeRef.current.setReloadState();
        });
    };

    const renderContent = () => {
        const availableFields = getPersonaCollectAvailableFields(originLabelData, industry);
        const isStandaloneFavorites = isFavoritesStandalone(originLabelData);
        const formRules: Record<string, IRuleDescriptor> = {
            company: {
                type: 'string',
                validate: (value: string) => {
                    return size(trim(value)) <= COMPANY_NAME;
                },
                message: ''
            }
        };
        return (
            <>
                <Form ref={formRef} value={formValue} rules={formRules} layout="vertical">
                    {availableFields.job && (
                        <Form.Item label={Intl.get('personal.job')} name="job">
                            <Radio.Group direction="horizontal">
                                {map(getJobs(originLabelData), (item) => {
                                    return (
                                        <Radio key={item.value} value={item.value}>
                                            {item.text}
                                        </Radio>
                                    );
                                })}
                            </Radio.Group>
                        </Form.Item>
                    )}
                    <Form.Item
                        label={Intl.get('personal.company.name')}
                        name="company"
                        contentRender={(valid, props) => {
                            return (
                                <div ref={containerRef}>
                                    <ErrorInput
                                        {...props}
                                        errMsg={Intl.get('max.length.error.message', {
                                            max: COMPANY_NAME
                                        })}
                                        inputState={valid ? undefined : 'error'}
                                        style={{ width: '100%' }}
                                        getPopupContainer={() =>
                                            containerRef.current || document.body
                                        }
                                    />
                                </div>
                            );
                        }}
                    />
                    {availableFields.industry && (
                        <Form.Item label={Intl.get('personal.company.industry')} name="industry">
                            <Combo
                                hasClear={false}
                                hasSearch={false}
                                items={getCorpIndustry(originLabelData)}
                                style={{ width: '100%' }}
                                dropdownStyle={{
                                    maxHeight: 200,
                                    overflow: 'auto'
                                }}
                                onValueChange={(val) => {
                                    setIndustry(val as string);
                                    // 管理需求为【单独配置】时与行业无关，保留已勾选项
                                    if (isStandaloneFavorites) {
                                        return;
                                    }
                                    // 切换所属行业时，应当置空所勾选的【管理需求】
                                    formRef.current.core.updateOption(
                                        assign(formRef.current.core.getValue(), {
                                            favorites: []
                                        })
                                    );
                                }}
                            />
                        </Form.Item>
                    )}
                    <Form.Item label={Intl.get('personal.company.scale')} name="corp_size">
                        <Radio.Group optionType="button">
                            {map(getCorpSizeItem(), (item) => {
                                return (
                                    <Radio
                                        className="corp-size-item"
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.value}
                                    </Radio>
                                );
                            })}
                        </Radio.Group>
                    </Form.Item>
                    {availableFields.favorites && (
                        <Form.Item label={Intl.get('personal.company.favorites')} name="favorites">
                            <Check.Group direction="horizontal">
                                {map(getFavoriteOptions(originLabelData, industry), (item) => {
                                    return (
                                        <Check value={item.value} key={item.value}>
                                            {item.text}
                                        </Check>
                                    );
                                })}
                            </Check.Group>
                        </Form.Item>
                    )}
                </Form>
                {!personalInfo.phone && hasFunctionPlatformPhone() && (
                    <>
                        <div className="item-label">{Intl.get('personal.phone')}</div>
                        <I18nPhone
                            className="item-phone"
                            value={phoneValue}
                            onBlur={(value) => {
                                setPhoneValue(value);
                            }}
                        />
                        <ValidCode
                            ref={validCodeRef}
                            onChange={(value) => {
                                setCodeValue(value);
                            }}
                            onBtnClick={dealValidCodeBtnClick}
                        />
                    </>
                )}
            </>
        );
    };

    const dealConfirm = () => {
        formRef.current.core
            .validate()
            .then(() => {
                const formValue = formRef.current.core.getValue();
                const availableFields = getPersonaCollectAvailableFields(
                    originLabelData,
                    formValue.industry
                );
                const personaData = {
                    company: formValue.company,
                    corp_size: formValue.corp_size,
                    ...getAvailablePersonaData(availableFields, {
                        job: formValue.job,
                        industry: formValue.industry,
                        favorites: formValue.favorites
                    })
                };
                const payload: Record<string, any> = {
                    ...personaData
                };
                if (isNumber(suiteId)) {
                    assign(payload, {
                        suite_id: suiteId
                    });
                }
                if (!personalInfo.phone && phoneValue) {
                    assign(payload, {
                        phone: phoneValue
                    });
                }
                if (!personalInfo.phone && codeValue) {
                    assign(payload, {
                        phone_code: codeValue
                    });
                }
                post(getApi(API.conf.update_persona, tenantId), payload).then((res) => {
                    onUpdate(get(res, 'data.persona'));
                });
            })
            .catch(EMPTY_FN);
    };

    return (
        <Dialog
            className="fx-info-complete-dialog"
            width={600}
            visible={visible}
            title={Intl.get('personal.info.complete')}
            onCancel={onClose}
            onOk={dealConfirm}
        >
            {renderContent()}
        </Dialog>
    );
};

export default InfoCompleteDialog;
